import {
  backButton,
  emitEvent,
  init as initSDK,
  miniApp,
  mockTelegramEnv,
  postEvent,
  restoreInitData,
  retrieveLaunchParams,
  setDebug,
  swipeBehavior,
  themeParams,
  type ThemeParams,
  themeParamsState,
  viewport,
} from '@telegram-apps/sdk-react'
import { TelegramMethodEnum } from '@/enums/telegram-method-enum'
import { TelegramEventEnum } from '@/enums/telegram-event-enum'
import { installFetchLogger } from './installFetchLogger'
import ReactGA from 'react-ga4'

const GA_ID = import.meta.env.VITE_GA_ID

/**
 * Initializes the application and configures its dependencies.
 */
export async function init(options: {
  debug: boolean
  eruda: boolean
  mockForMacOS: boolean
  skipFullscreen?: boolean
}): Promise<void> {
  setDebug(options.debug)
  initSDK()
  installFetchLogger()

  // Telegram for macOS has a ton of bugs, including cases, when the client doesn't
  // even response to the "web_app_request_theme" method. It also generates an incorrect
  // event for the "web_app_request_safe_area" method.
  if (options.mockForMacOS) {
    let firstThemeSent = false

    const noInsets = {
      left: 5,
      top: 20,
      right: 5,
      bottom: 5,
    }
    mockTelegramEnv({
      onEvent(event, next) {
        if (event[0] === TelegramMethodEnum.WebAppRequestTheme) {
          let tp: ThemeParams = {}
          if (firstThemeSent) {
            tp = themeParamsState()
          } else {
            firstThemeSent = true
            tp ||= retrieveLaunchParams().tgWebAppThemeParams
          }
          return emitEvent(TelegramEventEnum.ThemeChanged, {
            theme_params: tp,
          })
        }

        if (event[0] === TelegramMethodEnum.WebAppRequestContentSafeArea) {
          return emitEvent(TelegramEventEnum.ContentSafeAreaChanged, noInsets)
        }

        if (event[0] === TelegramMethodEnum.WebAppRequestSafeArea) {
          return emitEvent(TelegramEventEnum.SafeAreaChanged, noInsets)
        }

        next()
      },
    })
  }

  // Mount all components
  // mountBackButton.ifAvailable();
  restoreInitData()

  miniApp.mountSync()
  themeParams.mountSync()
  swipeBehavior.mount.ifAvailable()
  backButton.isSupported() && backButton.mount()

  // Async initializations
  await Promise.all([
    viewport.mount.isAvailable() &&
      viewport
        .mount({ timeout: 5000 })
        .then(async () => {
          viewport.bindCssVars.ifAvailable()
          miniApp.bindCssVars.ifAvailable()
          themeParams.bindCssVars.ifAvailable()
          swipeBehavior.disableVertical.ifAvailable()

          if (!options.skipFullscreen) {
            viewport.requestFullscreen.ifAvailable()
          }

          postEvent(TelegramMethodEnum.WebAppToggleOrientationLock, {
            locked: true,
          })
        })
        .catch((e: unknown) => {
          console.error('Something went wrong mounting viewport:', e)
          throw e
        }),
  ]).catch((e: unknown) => {
    console.error('Something went wrong mounting viewport:', e)
  })

  // Add Eruda if needed.
  if (options.eruda) {
    await import('eruda').then(({ default: eruda }) => {
      eruda.init()
      eruda.position({
        x: window.innerWidth - 50,
        y: viewport.safeAreaInsetTop() + viewport.contentSafeAreaInsetTop(),
      })
    })
  }

  miniApp.ready()
  miniApp.setBackgroundColor('#000000')
  GA_ID && ReactGA.initialize([{ trackingId: GA_ID }])
}
